import React, { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import Swal from "sweetalert2";

import "./categoryQuestions.css";

import { fetchThemeData } from "../../themeSlice";

import { useNavigate } from "react-router-dom";

import "./themeupdate.css";

import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";

import { updateThemeData } from "../../functions/updateThemeData";

import { ensureThemeJsonArray } from "../../../functions/themeAssets";



const MAX_TOPIC_LEN = 100;

const MAX_QUESTION_LEN = 200;

const MAX_WORD_LEN = 25;

const MIN_QUESTION_COUNT = 1;

const MAX_QUESTION_COUNT = 20;

const MIN_WORD_COUNT = 2;

const MAX_WORD_COUNT = 30;

const MAX_RETRIES = 3;



function sanitizeCountInput(value) {

  return String(value ?? "").replace(/[^\d]/g, "");

}



function clampCount(value, min, max, fallback) {

  const raw = String(value ?? "").trim();

  if (!raw) return fallback;

  const n = Math.trunc(Number(raw));

  if (!Number.isFinite(n)) return fallback;

  return Math.min(max, Math.max(min, n));

}



function clampText(value, maxLength) {

  return String(value ?? "").trim().slice(0, maxLength);

}



function parseAiResponse(responseText) {

  const clean = String(responseText || "")

    .replace(/```json/gi, "")

    .replace(/```/g, "")

    .trim();

  if (!clean) throw new Error("AI returned empty response.");



  const tryParse = (candidate) => {

    try {

      return JSON.parse(candidate);

    } catch {

      return null;

    }

  };



  const direct = tryParse(clean);

  if (Array.isArray(direct)) return direct;

  if (Array.isArray(direct?.questions)) return direct.questions;



  const firstBracket = clean.indexOf("[");

  const lastBracket = clean.lastIndexOf("]");

  if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {

    const extracted = tryParse(clean.slice(firstBracket, lastBracket + 1));

    if (Array.isArray(extracted)) return extracted;

  }



  throw new Error("Oops! Our AI had a little hiccup. Please try again.");

}



function normalizeWords(rawWords, wordCount) {

  const list = Array.isArray(rawWords)

    ? rawWords.map((w) => clampText(w, MAX_WORD_LEN)).filter(Boolean)

    : String(rawWords ?? "")

        .split(/[,;\n]+/)

        .map((w) => clampText(w, MAX_WORD_LEN))

        .filter(Boolean);

  const unique = [];

  const seen = new Set();

  for (const w of list) {

    const key = w.toLowerCase();

    if (seen.has(key)) continue;

    seen.add(key);

    unique.push(w);

  }

  return unique.slice(0, wordCount);

}



function toCategoryQuestion(raw, topicFallback, wordCount) {

  const topic_name = clampText(raw?.topic_name || topicFallback, MAX_TOPIC_LEN);

  const question_name = clampText(raw?.question_name, MAX_QUESTION_LEN);

  const words = normalizeWords(raw?.words, wordCount);

  return { topic_name, question_name, words, deleted: false };

}



function buildCategoryPrompt({ topic, questionCount, wordCount }) {

  return `You are creating content for "Category Rush", a sorting game. Players read a question and sort related words into categories.



TOPIC: "${topic}"



Generate exactly ${questionCount} question objects. Each object:

- topic_name: must be "${topic}"

- question_name: a short, clear prompt about items related to this topic (e.g. for office: "What things do you see in an office?"). Must end with ?

- words: a JSON array of exactly ${wordCount} distinct concrete nouns or short phrases (1?3 words) that fit the topic. No duplicates within one question.



Every object must match this schema:

{"topic_name":"${topic}","question_name":"<string>","words":["word1","word2",...]}



Rules:

- All ${questionCount} questions must relate to "${topic}" but use different question_name wording.

- Each words array must have exactly ${wordCount} items.

- question_name max 200 characters; each word max ${MAX_WORD_LEN} characters.

- Return ONLY a JSON array of ${questionCount} objects. No markdown, no code fences, no extra text.`;

}



async function callAI(prompt, token) {

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  if (!backendBase) throw new Error("Backend URL is not configured.");

  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {

    method: "POST",

    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },

    body: JSON.stringify({ prompt }),

  });

  const payload = await resp.json().catch(() => ({}));

  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");

  return payload?.response;

}



async function fetchCategoryQuestions({ topic, questionCount, wordCount, token }) {

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {

    const raw = await callAI(buildCategoryPrompt({ topic, questionCount, wordCount }), token);

    const parsed = parseAiResponse(raw);

    const normalized = parsed

      .map((q) => toCategoryQuestion(q, topic, wordCount))

      .filter((q) => q.question_name && q.words.length >= Math.min(2, wordCount));



    const strict = normalized.filter((q) => q.words.length === wordCount);

    const pool = strict.length >= questionCount ? strict : normalized;



    if (pool.length >= questionCount) {

      return pool.slice(0, questionCount);

    }



    if (attempt === MAX_RETRIES && pool.length > 0) {

      return pool;

    }

  }

  throw new Error(`Could not generate ${questionCount} questions after ${MAX_RETRIES} attempts.`);

}



function wordsToEditText(words) {

  return ensureThemeJsonArray(words).join("\n");

}



function parseWordsFromEditText(text, wordCount) {

  return normalizeWords(

    String(text ?? "")

      .split(/[\n,;]+/)

      .map((s) => s.trim())

      .filter(Boolean),

    wordCount

  );

}



const ThemeUpdate = () => {

  const dispatch = useDispatch();

  const adminToken = useSelector(selectAdminToken);

  const organizationId = useSelector(selectOrganizationId);

  const sessionId = useSelector(selectSessionId);

  const navigate = useNavigate();

  const { currentTheme, data, status } = useSelector((state) => state.theme);



  const [phase, setPhase] = useState("create");

  const [topicName, setTopicName] = useState("");

  const [questionCountInput, setQuestionCountInput] = useState("5");

  const [wordCountInput, setWordCountInput] = useState("5");

  const [questions, setQuestions] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [isGenerating, setIsGenerating] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  const [mode, setMode] = useState("view");

  const [editingIdx, setEditingIdx] = useState(null);

  const [editQuestionName, setEditQuestionName] = useState("");

  const [editWordsText, setEditWordsText] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  const [successMessage, setSuccessMessage] = useState("");



  useEffect(() => {

    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));

  }, [dispatch, currentTheme]);



  const questionCount = useMemo(

    () => clampCount(questionCountInput, MIN_QUESTION_COUNT, MAX_QUESTION_COUNT, 5),

    [questionCountInput]

  );

  const wordCount = useMemo(

    () => clampCount(wordCountInput, MIN_WORD_COUNT, MAX_WORD_COUNT, 5),

    [wordCountInput]

  );



  useEffect(() => {

    if (!data) return;

    const existing = ensureThemeJsonArray(data.questions);

    if (existing.length) {

      setQuestions(

        existing.map((q) => ({

          topic_name: clampText(q.topic_name, MAX_TOPIC_LEN),

          question_name: clampText(q.question_name, MAX_QUESTION_LEN),

          words: normalizeWords(q.words, MAX_WORD_COUNT),

          deleted: false,

        }))

      );

      setPhase("review");

      setCurrentIndex(0);

      setTopicName((prev) => prev || String(existing[0]?.topic_name || ""));

    }

    const qc = Number(data.question_count);

    const wc = Number(data.word_count);

    if (Number.isFinite(qc) && qc > 0) setQuestionCountInput(String(qc));

    if (Number.isFinite(wc) && wc > 0) setWordCountInput(String(wc));

  }, [data]);



  const visibleQuestions = useMemo(

    () => questions.map((q, idx) => ({ ...q, _idx: idx })).filter((q) => !q.deleted),

    [questions]

  );

  const currentQuestion = visibleQuestions[currentIndex] || null;

  const displayTopic = currentQuestion?.topic_name || topicName || "?";



  useEffect(() => {

    if (!visibleQuestions.length) {

      setCurrentIndex(0);

      return;

    }

    if (currentIndex > visibleQuestions.length - 1) {

      setCurrentIndex(visibleQuestions.length - 1);

    }

  }, [visibleQuestions, currentIndex]);



  const selectQuestion = (index) => {

    setCurrentIndex(index);

    setMode("view");

    setEditingIdx(null);

    setErrorMessage("");

  };



  const handleGenerate = async () => {

    const topic = clampText(topicName, MAX_TOPIC_LEN);

    if (!topic) {

      setErrorMessage("Please enter a topic name.");

      return;

    }

    if (!questionCountInput.trim() || !wordCountInput.trim()) {

      setErrorMessage("Please enter question count and word count.");

      return;

    }



    setErrorMessage("");

    setSuccessMessage("");

    setIsGenerating(true);

    try {

      const generated = await fetchCategoryQuestions({

        topic,

        questionCount,

        wordCount,

        token: adminToken,

      });

      if (!generated.length) throw new Error("No valid questions were generated.");

      setQuestions(generated);

      setCurrentIndex(0);

      setMode("view");

      setEditingIdx(null);

      setPhase("review");

      setSuccessMessage(`Generated ${generated.length} question(s).`);

    } catch (err) {

      setErrorMessage(err?.message || "Failed to generate content.");

    } finally {

      setIsGenerating(false);

    }

  };



  const startEdit = (q) => {

    if (!q) return;

    setEditingIdx(q._idx);

    setEditQuestionName(q.question_name);

    setEditWordsText(wordsToEditText(q.words));

    setMode("edit");

    setErrorMessage("");

  };



  const startAdd = () => {

    if (visibleQuestions.length >= MAX_QUESTION_COUNT) {

      setErrorMessage(`You can only keep up to ${MAX_QUESTION_COUNT} questions.`);

      return;

    }

    setErrorMessage("");

    setEditQuestionName("");

    setEditWordsText("");

    setEditingIdx(null);

    setMode("add");

  };



  const cancelEdit = () => {

    setMode("view");

    setEditingIdx(null);

    setErrorMessage("");

  };



  const saveEdit = () => {

    const topic = clampText(topicName, MAX_TOPIC_LEN);

    const nextName = clampText(editQuestionName, MAX_QUESTION_LEN);

    const nextWords = parseWordsFromEditText(editWordsText, wordCount);



    if (!nextName) {

      setErrorMessage("Question text cannot be empty.");

      return;

    }

    if (nextWords.length < MIN_WORD_COUNT) {

      setErrorMessage(`Add at least ${MIN_WORD_COUNT} words.`);

      return;

    }

    const rawWordParts = String(editWordsText ?? "")
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const tooLongWord = rawWordParts.find((w) => w.length > MAX_WORD_LEN);
    if (tooLongWord) {
      setErrorMessage(`Each word must be at most ${MAX_WORD_LEN} characters.`);
      return;
    }



    setErrorMessage("");

    const row = {

      topic_name: topic || clampText(currentQuestion?.topic_name, MAX_TOPIC_LEN),

      question_name: nextName,

      words: nextWords,

      deleted: false,

    };



    if (mode === "add") {

      setQuestions((prev) => [...prev, row]);

      setCurrentIndex(visibleQuestions.length);

      setMode("view");

      setEditingIdx(null);

      setSuccessMessage("Question added.");

      return;

    }



    setQuestions((prev) =>

      prev.map((q, idx) => (idx === editingIdx ? { ...q, ...row } : q))

    );

    setMode("view");

    setEditingIdx(null);

    setSuccessMessage("Question updated.");

  };



  const handleDelete = async (q) => {

    if (!q) return;

    const result = await Swal.fire({

      title: "Are you sure?",

      text: "Delete this question?",

      icon: "warning",

      showCancelButton: true,

      confirmButtonText: "Delete",

      cancelButtonText: "Cancel",

      confirmButtonColor: "#ef4444",

    });

    if (!result.isConfirmed) return;

    setQuestions((prev) => prev.map((row, idx) => (idx === q._idx ? { ...row, deleted: true } : row)));

    setMode("view");

    setEditingIdx(null);

    setSuccessMessage("Question removed.");

  };



  const handleSaveTheme = async () => {

    const ready = visibleQuestions.map(({ topic_name, question_name, words }) => ({

      topic_name: clampText(topic_name || topicName, MAX_TOPIC_LEN),

      question_name: clampText(question_name, MAX_QUESTION_LEN),

      words: normalizeWords(words, wordCount),

    }));



    if (!ready.length) {

      setErrorMessage("No questions to save.");

      return;

    }



    for (let i = 0; i < ready.length; i++) {

      if (!ready[i].question_name) {

        setErrorMessage(`Question ${i + 1}: text is required.`);

        return;

      }

      if (ready[i].words.length < MIN_WORD_COUNT) {

        setErrorMessage(`Question ${i + 1}: add at least ${MIN_WORD_COUNT} words.`);

        return;

      }

      const overLen = ready[i].words.find((w) => String(w).length > MAX_WORD_LEN);
      if (overLen) {
        setErrorMessage(
          `Question ${i + 1}: each word must be at most ${MAX_WORD_LEN} characters.`
        );
        return;
      }

    }



    setIsSaving(true);

    setErrorMessage("");

    setSuccessMessage("");

    try {

      await updateThemeData({

        payload: {

          data: {

            questions: ready,

            question_count: ready.length,

            word_count: wordCount,

          },

          organizationId: organizationId || "admin",

          sessionId: sessionId || "admin",

          currentTheme,

        },

        token: adminToken,

      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();

      Swal.fire("Success", "Questions saved to theme", "success");

      setSuccessMessage("Saved to theme.");

    } catch (err) {

      setErrorMessage(err?.message || "Failed to save questions.");

    } finally {

      setIsSaving(false);

    }

  };



  const handlePrev = () => {

    if (currentIndex <= 0) return;

    selectQuestion(currentIndex - 1);

  };



  const handleNext = () => {

    if (currentIndex >= visibleQuestions.length - 1) return;

    selectQuestion(currentIndex + 1);

  };



  const goToCreate = () => {

    setPhase("create");

    setMode("view");

    setEditingIdx(null);

    setErrorMessage("");

  };



  const setupLocked = isGenerating || isSaving;

  const modeLabel = mode === "edit" ? "Editing" : mode === "add" ? "Add question" : "Preview";



  const renderEditForm = () => (

    <div className="cr-edit-form">

      <div className="cr-field">

        <label htmlFor="cr-edit-question">Question</label>

        <input

          id="cr-edit-question"

          type="text"

          value={editQuestionName}

          onChange={(e) => setEditQuestionName(e.target.value)}

          placeholder="e.g. What items are commonly found in an office?"

          maxLength={MAX_QUESTION_LEN}

        />

      </div>

      <div className="cr-field">

        <label htmlFor="cr-edit-words">Words</label>

        <textarea

          id="cr-edit-words"

          value={editWordsText}

          onChange={(e) => setEditWordsText(e.target.value)}

          placeholder={"desk\nchair\ncomputer"}

        />

        <p className="cr-edit-hint">
          One word per line, or comma-separated. Target: {wordCount} words per question. Max{" "}
          {MAX_WORD_LEN} characters per word.
        </p>

      </div>

    </div>

  );



  if (status === "loading" && !data) {

    return (

      <div className="themeupdate-page">

        <div className="themeupdate-loading">Loading theme data?</div>

      </div>

    );

  }



  return (

    <div className="themeupdate-page">

      <div className="back-button-holder">

        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>

          <i className="fa-solid fa-arrow-left" /> Back

        </button>

      </div>



      <div className="cr-questions">

        <header className="cr-questions-header">

          <div className="cr-questions-header-text">

            <h1>Category Questions</h1>

            <p>Generate, preview, and edit sorting questions for this theme.</p>

          </div>

          {phase === "review" && visibleQuestions.length > 0 ? (

            <div className="cr-questions-header-actions">

              <button type="button" className="cr-btn cr-btn-ghost" onClick={goToCreate} disabled={setupLocked}>

                New topic / regenerate

              </button>

              <button

                type="button"

                className="cr-btn cr-btn-accent"

                onClick={handleSaveTheme}

                disabled={isSaving || mode !== "view"}

              >

                {isSaving ? "Saving?" : "Save to theme"}

              </button>

            </div>

          ) : null}

        </header>



        {(errorMessage || successMessage) && (

          <div>

            {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}

            {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}

          </div>

        )}



        <div className="cr-questions-layout">

          {/* Sidebar */}

          <aside className="cr-questions-sidebar">

            {phase === "create" ? (

              <>

                <div className="cr-sidebar-head">

                  <h2>Setup</h2>

                  <p className="cr-sidebar-topic">AI content generator</p>

                </div>

                <div className="cr-sidebar-setup">

                  <div className="cr-field">

                    <label htmlFor="cr-topic">Topic name</label>

                    <input

                      id="cr-topic"

                      type="text"

                      value={topicName}

                      onChange={(e) => {

                        setTopicName(e.target.value);

                        setErrorMessage("");

                      }}

                      placeholder="e.g. Office, Kitchen, Sports"

                      disabled={setupLocked}

                      maxLength={MAX_TOPIC_LEN}

                      autoComplete="off"

                    />

                  </div>

                  <div className="cr-inline-counts">

                    <div className="cr-field">

                      <label htmlFor="cr-q-count">Questions</label>

                      <input

                        id="cr-q-count"

                        type="text"

                        inputMode="numeric"

                        value={questionCountInput}

                        onChange={(e) => {

                          const raw = sanitizeCountInput(e.target.value);

                          if (!raw) {

                            setQuestionCountInput("");

                            return;

                          }

                          const n = Math.trunc(Number(raw) || 0);

                          setQuestionCountInput(String(Math.min(Math.max(0, n), MAX_QUESTION_COUNT)));

                        }}

                        onBlur={() =>

                          setQuestionCountInput(

                            String(clampCount(questionCountInput, MIN_QUESTION_COUNT, MAX_QUESTION_COUNT, 5))

                          )

                        }

                        disabled={setupLocked}

                      />

                    </div>

                    <div className="cr-field">

                      <label htmlFor="cr-w-count">Words each</label>

                      <input

                        id="cr-w-count"

                        type="text"

                        inputMode="numeric"

                        value={wordCountInput}

                        onChange={(e) => {

                          const raw = sanitizeCountInput(e.target.value);

                          if (!raw) {

                            setWordCountInput("");

                            return;

                          }

                          const n = Math.trunc(Number(raw) || 0);

                          setWordCountInput(String(Math.min(Math.max(0, n), MAX_WORD_COUNT)));

                        }}

                        onBlur={() =>

                          setWordCountInput(

                            String(clampCount(wordCountInput, MIN_WORD_COUNT, MAX_WORD_COUNT, 5))

                          )

                        }

                        disabled={setupLocked}

                      />

                    </div>

                  </div>

                  <div className="cr-sidebar-actions">

                    <button

                      type="button"

                      className="cr-btn cr-btn-accent cr-btn-block"

                      onClick={handleGenerate}

                      disabled={setupLocked}

                    >

                      {isGenerating ? "Generating?" : "Generate questions"}

                    </button>

                    {visibleQuestions.length > 0 ? (

                      <button

                        type="button"

                        className="cr-btn cr-btn-ghost cr-btn-block"

                        onClick={() => setPhase("review")}

                        disabled={setupLocked}

                      >

                        Review existing ({visibleQuestions.length})

                      </button>

                    ) : null}

                  </div>

                </div>

              </>

            ) : (

              <>

                <div className="cr-sidebar-head">

                  <h2>Topic</h2>

                  <p className="cr-sidebar-topic">{displayTopic}</p>

                  <p className="cr-sidebar-meta">

                    {visibleQuestions.length} question{visibleQuestions.length !== 1 ? "s" : ""} , {wordCount} words

                    each

                  </p>

                </div>

                <nav className="cr-question-list" aria-label="Questions">

                  {visibleQuestions.map((q, vi) => (

                    <button

                      key={q._idx}

                      type="button"

                      className={`cr-question-list-item${vi === currentIndex ? " is-active" : ""}`}

                      onClick={() => selectQuestion(vi)}

                    >

                      <div className="cr-question-list-item-num">Question {vi + 1}</div>

                      <div className="cr-question-list-item-text">

                        {q.question_name || "Untitled question"}

                      </div>

                      <div className="cr-question-list-item-words">

                        {ensureThemeJsonArray(q.words).length} words

                      </div>

                    </button>

                  ))}

                </nav>

                <div className="cr-sidebar-foot">

                  <button

                    type="button"

                    className="cr-btn cr-btn-ghost cr-btn-block"

                    onClick={goToCreate}

                    disabled={setupLocked}

                  >

                    New topic / regenerate

                  </button>

                </div>

              </>

            )}

          </aside>



          {/* Main panel */}

          <section className="cr-questions-main" aria-live="polite">

            {phase === "create" && !visibleQuestions.length ? (

              <div className="cr-empty-state">

                <p>Enter a topic and generate questions, or load existing content from your theme.</p>

              </div>

            ) : phase === "create" && visibleQuestions.length > 0 ? (

              <div className="cr-empty-state">

                <p>{visibleQuestions.length} question(s) ready. Open review to preview and edit.</p>

                <button type="button" className="cr-btn cr-btn-primary" onClick={() => setPhase("review")}>

                  Open review

                </button>

              </div>

            ) : !currentQuestion && mode === "view" ? (

              <div className="cr-empty-state">

                <p>No questions yet. Generate content or add a question manually.</p>

                <button type="button" className="cr-btn cr-btn-primary" onClick={goToCreate}>

                  Go to setup

                </button>

              </div>

            ) : (

              <>

                <div className="cr-main-toolbar">

                  <div className="cr-main-toolbar-left">

                    <span className={`cr-mode-badge ${mode}`}>{modeLabel}</span>

                    {mode === "view" && visibleQuestions.length > 0 ? (

                      <span className="cr-counter">

                        {currentIndex + 1} / {visibleQuestions.length}

                      </span>

                    ) : null}

                  </div>

                  <div className="cr-main-toolbar-actions">

                    {mode === "view" && currentQuestion ? (

                      <>

                        <button type="button" className="cr-btn cr-btn-ghost" onClick={() => startEdit(currentQuestion)}>

                          Edit

                        </button>

                        <button

                          type="button"

                          className="cr-btn cr-btn-outline-danger"

                          onClick={() => handleDelete(currentQuestion)}

                        >

                          Delete

                        </button>

                      </>

                    ) : null}

                    {(mode === "edit" || mode === "add") && (

                      <>

                        <button type="button" className="cr-btn cr-btn-accent" onClick={saveEdit}>

                          {mode === "add" ? "Add question" : "Save changes"}

                        </button>

                        <button type="button" className="cr-btn cr-btn-ghost" onClick={cancelEdit}>

                          Cancel

                        </button>

                      </>

                    )}

                  </div>

                </div>



                <div className="cr-main-body">

                  {mode === "view" && currentQuestion ? (

                    <>

                      <h3 className="cr-view-question">{currentQuestion.question_name}</h3>

                      <div className="cr-words-grid">

                        {ensureThemeJsonArray(currentQuestion.words).map((word, wi) => (

                          <span key={`${word}-${wi}`} className="cr-word-chip">

                            {word}

                          </span>

                        ))}

                      </div>

                    </>

                  ) : null}



                  {mode === "edit" ? renderEditForm() : null}



                  {mode === "add" ? (

                    <>

                      {currentQuestion ? (

                        <div className="cr-add-reference">

                          <p className="cr-add-reference-label">Current question (reference)</p>

                          <p className="cr-view-question">{currentQuestion.question_name}</p>

                          <div className="cr-words-grid">

                            {ensureThemeJsonArray(currentQuestion.words).map((word, wi) => (

                              <span key={`ref-${word}-${wi}`} className="cr-word-chip">

                                {word}

                              </span>

                            ))}

                          </div>

                        </div>

                      ) : null}

                      {renderEditForm()}

                    </>

                  ) : null}

                </div>



                {mode === "view" && visibleQuestions.length > 0 ? (

                  <footer className="cr-main-footer">

                    <div className="cr-main-footer-group">

                      <button

                        type="button"

                        className="cr-btn cr-btn-ghost"

                        onClick={handlePrev}

                        disabled={currentIndex <= 0}

                      >

                        Previous

                      </button>

                      <button

                        type="button"

                        className="cr-btn cr-btn-ghost"

                        onClick={handleNext}

                        disabled={currentIndex >= visibleQuestions.length - 1}

                      >

                        Next

                      </button>

                    </div>

                    <div className="cr-main-footer-group">

                      <button

                        type="button"

                        className="cr-btn cr-btn-primary"

                        onClick={startAdd}

                        disabled={visibleQuestions.length >= MAX_QUESTION_COUNT}

                      >

                        Add question

                      </button>

                      <button

                        type="button"

                        className="cr-btn cr-btn-accent"

                        onClick={handleSaveTheme}

                        disabled={isSaving}

                      >

                        {isSaving ? "Saving?" : "Save to theme"}

                      </button>

                    </div>

                  </footer>

                ) : null}

              </>

            )}

          </section>

        </div>

      </div>

    </div>

  );

};



export default ThemeUpdate;

